import { useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Check, Sparkles, Zap, Crown, Rocket, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PLANS, type Plan } from "@/lib/plans";
import { useCountUp } from "@/hooks/use-count-up";
import { getFreePlanSlots } from "@/lib/site-stats.functions";
import { FreePlanSlotsBadge, type SlotsInfo } from "@/components/FreePlanSlotsBadge";

const ICONS: Record<string, typeof Sparkles> = {
  free: Sparkles,
  starter: Rocket,
  pro: Zap,
  unlimited: Crown,
};

const DZD_MAP: Record<number, number> = { 9.99: 2500, 19.99: 5000, 34.99: 8750 };

interface PricingPlanGridProps {
  currentTier?: string;
  kycVerified?: boolean;
  showBadge?: boolean;
  showTitle?: boolean;
  showFootnote?: boolean;
  className?: string;
}

export function PricingPlanGrid({
  currentTier = "free",
  kycVerified = false,
  showBadge = true,
  showTitle = true,
  showFootnote = true,
  className,
}: PricingPlanGridProps) {
  const { t } = useTranslation();
  const [currency, setCurrency] = useState<"USD" | "DZD">("USD");
  const fetchSlots = useServerFn(getFreePlanSlots);
  const { data: slots } = useQuery({
    queryKey: ["free-plan-slots"],
    queryFn: () => fetchSlots(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    <div className={cn("relative w-full text-foreground", className)}>
      {showTitle && (
        <div className="text-center space-y-5 opacity-0 [animation:fade-in_0.7s_ease-out_forwards]">
          {showBadge && (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium tracking-wider uppercase text-primary/90 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              {t("pricingPage.badge")}
            </div>
          )}
          <h1 className="text-4xl sm:text-6xl font-semibold tracking-[-0.02em] leading-[1.15] text-foreground pb-1">
            {t("pricingPage.title")}
          </h1>
          <p className="text-base sm:text-lg text-foreground/80 max-w-xl mx-auto leading-relaxed">
            {t("pricingPage.subtitle")}
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            {t("pricingIntro.blurb")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 pt-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/8 bg-white/[0.03] px-2.5 py-1 backdrop-blur-sm">
              <ShieldCheck className="h-3 w-3 text-primary" /> {t("pricingIntro.stripeSecured")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/8 bg-white/[0.03] px-2.5 py-1 backdrop-blur-sm">
              <Check className="h-3 w-3 text-primary" /> {t("pricingIntro.cancelAnytime")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/8 bg-white/[0.03] px-2.5 py-1 backdrop-blur-sm">
              <Check className="h-3 w-3 text-primary" /> {t("pricingIntro.noHiddenFees")}
            </span>
          </div>
        </div>
      )}

      <div className="flex justify-center py-6">
        <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1 backdrop-blur-sm">
          {(["USD", "DZD"] as const).map((c) => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              className={cn(
                "rounded-full px-4 py-1.5 text-xs font-semibold transition-all",
                currency === c
                  ? "bg-primary text-primary-foreground shadow-[0_0_20px_hsl(var(--primary)/0.4)]"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 items-stretch">
        {PLANS.map((p, i) => (
          <PlanCard
            key={p.id}
            plan={p}
            current={p.id === currentTier}
            kycVerified={kycVerified}
            index={i}
            slots={p.id === "free" ? slots ?? null : null}
            currency={currency}
          />
        ))}
      </div>

      {showFootnote && (
        <div className="text-center text-xs text-muted-foreground/80 opacity-0 [animation:fade-in_0.6s_ease-out_0.6s_forwards]">
          {t("pricingPage.footnote")}
        </div>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  current,
  kycVerified,
  index,
  slots,
  currency,
}: {
  plan: Plan;
  current: boolean;
  kycVerified: boolean;
  index: number;
  slots: SlotsInfo | null;
  currency: "USD" | "DZD";
}) {
  const Icon = ICONS[plan.id] ?? Sparkles;
  const [loading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const planName = t(`pricingPage.plans.${plan.id}.name`);
  const tagline = t(`pricingPage.plans.${plan.id}.tagline`);
  const features = t(`pricingPage.plans.${plan.id}.features`, { returnObjects: true }) as string[];

  const cardRef = useRef<HTMLDivElement>(null);
  const animatedPrice = useCountUp(plan.price, 900 + index * 100);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  const handleUpgrade = () => {
    if (plan.id === "free") return;
    navigate({ to: "/checkout/$plan", params: { plan: plan.id } });
  };

  const handleManage = () => {
    if (plan.id === "free") return;
    navigate({ to: "/checkout/$plan", params: { plan: plan.id } });
  };

  const isDzd = currency === "DZD" && plan.price > 0;
  const dzdPrice = DZD_MAP[plan.price] ?? Math.round(plan.price * 250);
  const priceStr = plan.price === 0 ? "0" : isDzd ? dzdPrice.toLocaleString() : animatedPrice.toFixed(2);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMove}
      style={{ animationDelay: `${100 + index * 100}ms` }}
      className={cn(
        "group relative flex h-full flex-col rounded-2xl border bg-card/70 backdrop-blur-xl p-6 text-start",
        "transition-all duration-500 ease-out hover:-translate-y-1.5",
        "opacity-0 [animation:fade-in_0.6s_ease-out_forwards]",
        plan.highlight
          ? "border-primary/50 shadow-[0_0_0_1px_color-mix(in_oklch,var(--primary)_20%,transparent),0_30px_80px_-30px_color-mix(in_oklch,var(--primary)_55%,transparent)] hover:shadow-[0_0_0_1px_color-mix(in_oklch,var(--primary)_30%,transparent),0_40px_100px_-25px_color-mix(in_oklch,var(--primary)_70%,transparent)]"
          : "border-border/60 hover:border-primary/30 hover:shadow-[0_20px_60px_-20px_color-mix(in_oklch,var(--primary)_25%,transparent)]",
      )}
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(320px circle at var(--mx,50%) var(--my,50%), color-mix(in oklch, var(--primary) 18%, transparent), transparent 60%)",
        }}
        aria-hidden
      />

      {plan.highlight && (
        <div
          className="pointer-events-none absolute -inset-px rounded-2xl [background:conic-gradient(from_var(--a,0deg),transparent_0%,color-mix(in_oklch,var(--primary)_60%,transparent)_20%,transparent_40%)] opacity-60 [mask:linear-gradient(#000,#000)_content-box,linear-gradient(#000,#000)] [mask-composite:exclude] p-px animate-[spin_6s_linear_infinite]"
          style={{ WebkitMaskComposite: "xor" as string }}
          aria-hidden
        />
      )}

      {plan.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-primary to-primary/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground shadow-[0_8px_24px_-6px_color-mix(in_oklch,var(--primary)_60%,transparent)]">
          {t("pricingPage.popular")}
        </span>
      )}

      <div className="relative flex items-center gap-2">
        <div
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3",
            plan.highlight
              ? "bg-gradient-to-br from-primary/25 to-primary/5 text-primary ring-1 ring-primary/30 shadow-[0_8px_20px_-8px_color-mix(in_oklch,var(--primary)_60%,transparent)]"
              : "bg-muted text-foreground/70 ring-1 ring-border/50",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="font-semibold text-base">{planName}</div>
        {current && (
          <span className="ms-auto rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-500 ring-1 ring-emerald-500/30">
            {t("pricingPage.current")}
          </span>
        )}
      </div>

      <div className="relative mt-5 flex items-baseline gap-1">
        <span className="text-5xl font-semibold tracking-tight tabular-nums bg-gradient-to-b from-foreground to-foreground/60 bg-clip-text text-transparent">
          {isDzd ? priceStr : `$${priceStr}`}
        </span>
        <span className="text-sm text-muted-foreground">
          {isDzd ? " DZD" : ""}
          {plan.price === 0 ? "" : t("pricingPage.monthly")}
        </span>
      </div>
      {isDzd && (
        <p className="relative mt-1 text-[11px] text-muted-foreground/70 tabular-nums">≈ ${plan.price.toFixed(2)} USD</p>
      )}
      <p className="relative mt-1.5 text-xs font-medium text-primary">
        {plan.price === 0
          ? t("pricingPage.freeForever")
          : plan.limit === null
            ? t("pricingPage.perProjectUnlimited")
            : t("pricingPage.perProject", { value: (plan.price / plan.limit).toFixed(2) })}
      </p>
      <p className="relative mt-1 text-sm text-muted-foreground">{tagline}</p>


      {plan.id === "free" && slots ? (
        <div className="relative mt-4">
          <FreePlanSlotsBadge slots={slots} />
        </div>
      ) : null}

      <ul className="relative mt-6 space-y-2.5 text-sm">
        {features.map((f, fi) => (
          <li
            key={f}
            style={{ animationDelay: `${300 + index * 100 + fi * 60}ms` }}
            className="flex items-start gap-2 opacity-0 [animation:fade-in_0.5s_ease-out_forwards]"
          >
            <span
              className={cn(
                "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                plan.highlight ? "bg-primary/20 text-primary" : "bg-primary/10 text-primary",
              )}
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="relative mt-auto pt-6 border-t border-border/60">
        {plan.id === "free" && !kycVerified ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground text-center">
              {t("pricingPage.freeVerifyHint", {
                defaultValue: "Verify your identity to unlock your 1 free project.",
              })}
            </p>
            <Button asChild className="w-full" variant="outline">
              <Link to="/verify">
                <ShieldAlert className="me-2 h-4 w-4" />
                {t("pricingPage.freeVerifyCta", { defaultValue: "Verify to unlock 1 free project" })}
              </Link>
            </Button>
          </div>
        ) : current ? (
          plan.price === 0 ? (
            <Button className="w-full" variant="secondary" disabled>
              {t("pricingPage.currentPlan")}
            </Button>
          ) : (
            <Button className="w-full" variant="outline" onClick={handleManage} disabled={loading}>
              {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
              {plan.id === "starter" ? t("pricingPage.upgradeToStarter") : t("pricingPage.managePlan")}
            </Button>
          )
        ) : plan.price === 0 ? (
          <Button className="w-full" variant="outline" disabled>
            {t("pricingPage.included")}
          </Button>
        ) : (
          <Button
            className={cn(
              "relative w-full overflow-hidden group/btn",
              plan.highlight && "bg-gradient-to-r from-primary to-primary/85 hover:shadow-[0_10px_30px_-8px_color-mix(in_oklch,var(--primary)_60%,transparent)]",
            )}
            variant={plan.highlight ? "default" : "outline"}
            onClick={handleUpgrade}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" /> {t("pricingPage.redirecting")}
              </>
            ) : (
              <>
                <span className="relative z-10">{t("pricingPage.upgradeTo", { name: planName })}</span>
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full" aria-hidden />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
